import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, getCurrentFinancialYear } from "../../../../utils/commonUtil";
import EditExpense from "../../MyCashFlow/EditExpense";

interface ICashFlowExpense {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  propertyManagment: number;
  serviceContranct: number;
  rates: number;
  bodyCorp: number;
  insurance: number;
  repairs: number;
  landLease: number;
  garden: number;
  cleaning: number;
  other: number;
}

const Expense: React.FC<{
  cashFlowData: any;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  isShowingTotal?: boolean;
}> = ({ cashFlowData, year, isEditable, setIsEditable, isShowingTotal }) => {
  const [transformedData, setTransformedData] = useState<ICashFlowExpense[]>([]);
  const currentYear = "20" + Number(getCurrentFinancialYear()).toString();

  useEffect(() => {
    if (cashFlowData) {
      const actual = isShowingTotal
        ? cashFlowData.CashflowPropertyExpense?.Actuals?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPropertyExpensePortion?.Actuals?.find((item: any) => item.Year === year) || {};
      const forecast = isShowingTotal
        ? cashFlowData.CashflowPropertyExpense?.Forecast?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPropertyExpensePortion?.Forecast?.find((item: any) => item.Year === year) || {};
      const variance = isShowingTotal
        ? cashFlowData.CashflowPropertyExpense?.Variance?.find((item: any) => item.Year === year) || {}
        : cashFlowData.CashflowPropertyExpensePortion?.Variance?.find((item: any) => item.Year === year) || {};

      setTransformedData(
        [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }: { type: string; data: any }) => ({
          street: cashFlowData.PortfolioTrackerDetailLabel.StreetAddress || "",
          suburb: cashFlowData.PortfolioTrackerDetailLabel.Suburb || "",
          state: cashFlowData.PortfolioTrackerDetailLabel.State || "",
          postcode: cashFlowData.PortfolioTrackerDetailLabel?.Postcode || "",
          country: cashFlowData.PortfolioTrackerDetailLabel?.Country || "",
          type,
          propertyManagment: Number(data.PropertyMgmt) || 0,
          serviceContranct: Number(data.ServiceCont) || 0,
          rates: Number(data.Rates) || 0,
          bodyCorp: Number(data.BodyCorp) || 0,
          insurance: Number(data.Insurance) || 0,
          repairs: Number(data.Repairs) || 0,
          landLease: Number(data.LandLease) || 0,
          garden: Number(data.GardenFee) || 0,
          cleaning: Number(data.Cleaning) || 0,
          other: Number(data.Other) || 0,
        }))
      );
    }
  }, [cashFlowData, year]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  return isEditable ? (
    <EditExpense
      cashFlowData={undefined}
      setIsEditable={setIsEditable}
      onCancel={() => setIsEditable(false)}
      portfolioTrackerId={cashFlowData.PortfolioTrackerDetailLabel.PortfolioTrackerId}
      propertyName={`${cashFlowData.PortfolioTrackerDetailLabel.StreetAddress}, ${cashFlowData.PortfolioTrackerDetailLabel.Suburb}, ${cashFlowData.PortfolioTrackerDetailLabel.State}, ${cashFlowData.PortfolioTrackerDetailLabel.Postcode} (${cashFlowData.PortfolioTrackerDetailLabel.Country})`}
    />
  ) : (
    transformedData && (
      <DataTable
        value={transformedData}
        rowGroupMode="rowspan"
        groupRowsBy="street"
        sortField="street"
        sortMode="single"
        sortOrder={1}
        size="small"
      >
        <Column
          field="street"
          header="Address/property name"
          body={(rowData) => (
            <div className="flex items-center">
              <span>{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
            </div>
          )}
        />
        <Column field="type" header="Type" body={(rowData) => rowData.type} />
        <Column
          field="propertyManagment"
          header="Property mngmt."
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.propertyManagment)
          }
          align="right"
        />
        <Column
          field="serviceContranct"
          header="Service contracts"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.serviceContranct)
          }
          align="right"
        />
        <Column
          field="rates"
          header="Rates"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast") ? "" : currencyTemplate(rowData.rates)
          }
          align="right"
        />
        <Column
          field="bodyCorp"
          header="Body corp."
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.bodyCorp)
          }
          align="right"
        />
        <Column
          field="insurance"
          header="Insurance"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.insurance)
          }
          align="right"
        />
        <Column
          field="repairs"
          header="Repairs"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.repairs)
          }
          align="right"
        />
        <Column
          field="landLease"
          header="Land lease"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.landLease)
          }
          align="right"
        />
        <Column
          field="garden"
          header="Garden"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast") ? "" : currencyTemplate(rowData.garden)
          }
          align="right"
        />
        <Column
          field="cleaning"
          header="Cleaning"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast")
              ? ""
              : currencyTemplate(rowData.cleaning)
          }
          align="right"
        />
        <Column
          field="other"
          header="Other"
          body={(rowData) =>
            !(Number(currentYear) >= Number(year) || rowData.type == "Forecast") ? "" : currencyTemplate(rowData.other)
          }
          align="right"
        />
      </DataTable>
    )
  );
};

export default Expense;
